import { useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, Button, useToast } from '../components/ui';
import { api } from '../lib/api';

/**
 * Owner Outreach — AI-drafted, owner-personalized prospecting letters/emails.
 *
 * Drafting never sends anything. Email drafts can be pushed to GHL one at a
 * time — each push needs its own explicit confirm, then the backend upserts the
 * owner as a GHL contact and sends the (possibly edited) draft through GHL's
 * email pipe, so sends ride the warmed GHL domain with conversation history.
 */

const PROPERTY_TYPES = [
  ['', 'Any type'],
  ['industrial', 'Industrial'],
  ['retail', 'Retail'],
  ['office', 'Office'],
  ['multifamily', 'Multifamily'],
  ['land', 'Land'],
  ['mixed_use', 'Mixed use'],
] as const;

const MATURITY_WINDOWS = [
  ['', 'Any / ignore loans'],
  ['90', 'Maturing ≤ 90 days'],
  ['120', 'Maturing ≤ 120 days'],
  ['180', 'Maturing ≤ 180 days'],
  ['365', 'Maturing ≤ 12 months'],
] as const;

interface Draft { propertyId?: string; ownerName?: string; subject?: string; body?: string }

/** Slim mirror of the backend OutreachTarget — only what the push flow needs. */
interface Target {
  propertyId: string;
  property: { address: string | null; city: string | null; state: string | null };
  owner: {
    name: string;
    phone: string | null;
    phoneNote: 'DNC' | null;
    email: string | null;
    mailingAddress: string | null;
  };
}

type PushState =
  | { status: 'idle' }
  | { status: 'confirm' }
  | { status: 'sending' }
  | { status: 'sent'; ghlUrl: string | null }
  | { status: 'error'; error: string };

export default function OwnerOutreach() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [targets, setTargets] = useState<Target[]>([]);
  // Channel the CURRENT batch was drafted with (the form may change afterwards).
  const [batchChannel, setBatchChannel] = useState<'email' | 'letter'>('email');
  const [pushStates, setPushStates] = useState<Record<string, PushState>>({});
  const [form, setForm] = useState({
    city: '', propertyType: '', minBuildingSF: '', soldAfter: '', soldBefore: '',
    maturityDays: '', limit: '5', channel: 'email' as 'email' | 'letter', angle: '',
  });

  const keyOf = (d: Draft, i: number) => d.propertyId || `i${i}`;
  const targetFor = (d: Draft): Target | undefined =>
    targets.find((t) => t.propertyId === d.propertyId);
  const setPush = (key: string, ps: PushState) =>
    setPushStates((m) => ({ ...m, [key]: ps }));
  const updateDraft = (i: number, patch: Partial<Draft>) =>
    setDrafts((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const run = async () => {
    setBusy(true);
    setDrafts([]);
    setTargets([]);
    setPushStates({});
    const channel = form.channel;
    try {
      const filter: Record<string, any> = { limit: Math.min(10, Math.max(1, Number(form.limit) || 5)) };
      if (form.city.trim()) filter.city = form.city.trim();
      if (form.propertyType) filter.propertyType = form.propertyType;
      if (form.minBuildingSF) filter.minBuildingSF = Number(form.minBuildingSF);
      if (form.soldAfter) filter.soldAfter = form.soldAfter;
      if (form.soldBefore) filter.soldBefore = form.soldBefore;
      if (form.maturityDays) filter.hasLoanMaturingWithinDays = Number(form.maturityDays);
      const res: any = await api.agentOwnerOutreach({
        filter,
        channel,
        angle: form.angle.trim() || undefined,
      });
      setDrafts(res.drafts || []);
      setTargets(res.targets || []);
      setBatchChannel(channel);
      if (!res.drafts?.length) toast.info('No drafts', 'No owners matched that filter.');
    } catch (e: any) {
      toast.error('Drafting failed', e?.message);
    } finally {
      setBusy(false);
    }
  };

  const copyDraft = async (d: Draft) => {
    const text = `${d.subject ? `Subject: ${d.subject}\n\n` : ''}${d.body || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Draft copied', d.ownerName);
    } catch {
      toast.error('Copy failed');
    }
  };

  const pushToGhl = async (d: Draft, i: number) => {
    const key = keyOf(d, i);
    const t = targetFor(d);
    const email = t?.owner.email;
    if (!d.propertyId || !t || !email) return;
    if (!(d.subject || '').trim()) {
      toast.error('Subject required', 'Add a subject line before sending.');
      return;
    }
    if (!(d.body || '').trim()) {
      toast.error('Body required', 'The draft body is empty.');
      return;
    }
    setPush(key, { status: 'sending' });
    try {
      const res: any = await api.agentOutreachPushToGhl({
        propertyId: d.propertyId,
        ownerName: (d.ownerName || t.owner.name || 'Property Owner').trim(),
        ownerEmail: email,
        ownerPhone: t.owner.phone || undefined,
        propertyAddress: [t.property.address, t.property.city].filter(Boolean).join(', '),
        subject: (d.subject || '').trim(),
        body: (d.body || '').trim(),
      });
      setPush(key, { status: 'sent', ghlUrl: res.ghlUrl || null });
      toast.success('Sent via GHL', email);
    } catch (e: any) {
      setPush(key, { status: 'error', error: e?.message || 'Push failed' });
      toast.error('GHL push failed', e?.message);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-400';
  const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400 mb-1';

  const bodyRows = (body: string) =>
    Math.min(20, Math.max(6, body.split('\n').length + Math.ceil(body.length / 110)));

  return (
    <Layout>
      <div>
        <div className="max-w-[1600px] pb-4">
          <h1 className="font-display text-[32px] leading-[1.06] font-semibold uppercase tracking-[-.01em] text-gray-900">Owner Outreach</h1>
          <p className="text-gray-500 text-sm mt-1">
            AI-drafted, owner-personalized prospecting - review and edit, then{' '}
            <span className="font-medium text-gray-700">push each email through GHL with a per-draft confirm.</span>
          </p>
        </div>
      </div>

      <div className="max-w-[1600px] pt-4 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Who are we writing to?</CardTitle>
            <p className="text-xs text-gray-400 mt-0.5">Filter owners from your property database. DNC-flagged phones are never surfaced.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} placeholder="e.g. Fontana" value={form.city}
                  onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Property type</label>
                <select className={inputCls} value={form.propertyType}
                  onChange={(e) => setForm(f => ({ ...f, propertyType: e.target.value }))}>
                  {PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Min building SF</label>
                <input className={inputCls} type="number" min="0" placeholder="e.g. 20000" value={form.minBuildingSF}
                  onChange={(e) => setForm(f => ({ ...f, minBuildingSF: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Loan maturity</label>
                <select className={inputCls} value={form.maturityDays}
                  onChange={(e) => setForm(f => ({ ...f, maturityDays: e.target.value }))}>
                  {MATURITY_WINDOWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Bought after</label>
                <input className={inputCls} type="date" value={form.soldAfter}
                  onChange={(e) => setForm(f => ({ ...f, soldAfter: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Bought before</label>
                <input className={inputCls} type="date" value={form.soldBefore}
                  onChange={(e) => setForm(f => ({ ...f, soldBefore: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Max drafts</label>
                <input className={inputCls} type="number" min="1" max="10" value={form.limit}
                  onChange={(e) => setForm(f => ({ ...f, limit: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Format</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden divide-x divide-gray-300">
                  {(['email', 'letter'] as const).map(c => (
                    <button key={c}
                      onClick={() => setForm(f => ({ ...f, channel: c }))}
                      className={`flex-1 px-3 py-2 text-sm capitalize ${form.channel === c ? 'bg-gray-100 text-gray-900 font-medium' : 'bg-white text-gray-500 hover:text-gray-700'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2 lg:col-span-4">
                <label className={labelCls}>Angle (optional)</label>
                <input className={inputCls} placeholder='e.g. "1031 buyers are active in your submarket"' value={form.angle}
                  onChange={(e) => setForm(f => ({ ...f, angle: e.target.value }))} />
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Button onClick={run} disabled={busy}>
                {busy ? 'Drafting…' : '✨ Draft outreach'}
              </Button>
              {busy && <p className="text-xs text-gray-400">Finding owners and writing — usually 20–40 seconds.</p>}
              {targets.length > 0 && !busy && (
                <p className="text-xs text-gray-400">{targets.length} owner{targets.length === 1 ? '' : 's'} matched.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {drafts.length > 0 && (
          <div className="space-y-4">
            {drafts.map((d, i) => {
              const key = keyOf(d, i);
              const t = targetFor(d);
              const email = t?.owner.email || null;
              const ps = pushStates[key] || { status: 'idle' as const };
              const canPush = batchChannel === 'email' && !!d.propertyId && !!t;
              return (
                <Card key={key}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <CardTitle>{d.ownerName || t?.owner.name || 'Owner'}</CardTitle>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {[t?.property.address, t?.property.city].filter(Boolean).join(', ') || 'Property on file'}
                          {batchChannel === 'email' && (
                            email
                              ? <span> · To: <span className="text-gray-500">{email}</span></span>
                              : <span> · <span className="text-warning">No owner email on file</span></span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => copyDraft(d)}>Copy</Button>
                        {canPush && ps.status === 'idle' && (
                          <Button size="sm" disabled={!email} title={email ? undefined : 'No owner email on file'}
                            onClick={() => setPush(key, { status: 'confirm' })}>
                            Push to GHL
                          </Button>
                        )}
                        {canPush && ps.status === 'sent' && (
                          <span className="text-xs font-medium text-success">✓ Sent via GHL</span>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {batchChannel === 'email' && (
                      <div className="mb-3">
                        <label className={labelCls}>Subject</label>
                        <input className={inputCls} value={d.subject || ''} placeholder="Subject"
                          disabled={ps.status === 'sending' || ps.status === 'sent'}
                          onChange={(e) => updateDraft(i, { subject: e.target.value })} />
                      </div>
                    )}
                    <textarea
                      className="w-full text-sm text-gray-700 leading-relaxed rounded-lg border border-transparent hover:border-gray-200 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-y bg-transparent px-2 py-1.5 -mx-2"
                      rows={bodyRows(d.body || '')}
                      value={d.body || ''}
                      disabled={ps.status === 'sending' || ps.status === 'sent'}
                      onChange={(e) => updateDraft(i, { body: e.target.value })}
                    />

                    {canPush && ps.status === 'confirm' && email && (
                      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 px-3 py-2.5">
                        <p className="text-xs text-gray-600 flex-1 min-w-[220px]">
                          Sends this email to <span className="font-medium text-gray-800">{email}</span> through
                          your GHL domain - creates/updates the contact and logs it to conversation history.
                        </p>
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => pushToGhl(d, i)}>Send via GHL</Button>
                          <Button variant="outline" size="sm" onClick={() => setPush(key, { status: 'idle' })}>Cancel</Button>
                        </div>
                      </div>
                    )}
                    {ps.status === 'sending' && (
                      <p className="mt-3 text-xs text-gray-400">Sending through GHL…</p>
                    )}
                    {ps.status === 'sent' && (
                      <p className="mt-3 text-xs text-gray-500">
                        Delivered to GHL.{' '}
                        {ps.ghlUrl && (
                          <a className="text-primary-500 hover:underline font-medium" href={ps.ghlUrl} target="_blank" rel="noreferrer">
                            Open contact in GHL ↗
                          </a>
                        )}
                      </p>
                    )}
                    {ps.status === 'error' && (
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <p className="text-xs text-red-600 flex-1 min-w-[220px]">{ps.error}</p>
                        <Button variant="outline" size="sm" onClick={() => setPush(key, { status: 'confirm' })}>Try again</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            <p className="text-[11px] text-gray-400">
              {batchChannel === 'email'
                ? 'Edit freely - what you see is what sends. Nothing goes out without the per-draft "Send via GHL" confirm.'
                : 'Letters: review, copy, and print for mailing - nothing sends from this page.'}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
